"use client"

import { createContext, useState, useContext } from "react"

const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

// Translation object
const translations = {
  en: {
    // Header
    "header.home": "Home",
    "header.videos": "Videos",
    "header.dashboard": "Dashboard",
    "header.adminPanel": "Admin Panel",
    "header.login": "Login",
    "header.register": "Register",
    "header.logout": "Logout",

    // Home Page
    "home.title": "Make a Difference Today",
    "home.subtitle":
      "Join our community of donors and help us create positive change. Every ₹100 donation makes a meaningful impact.",
    "home.getStarted": "Get Started",
    "home.signIn": "Sign In",
    "home.whyChoose": "Why Choose Our Platform?",
    "home.whyChooseDesc":
      "We provide a secure, transparent, and easy-to-use platform for making donations and tracking your impact.",
    "home.fixedDonations": "Fixed Donations",
    "home.fixedDonationsDesc": "Simple ₹100 fixed donation amount ensures transparency and consistency.",
    "home.community": "Community",
    "home.communityDesc": "Join a community of like-minded individuals making a positive impact.",
    "home.transparency": "Transparency",
    "home.transparencyDesc": "Track your donations and see monthly reports of community impact.",
    "home.updates": "Updates",
    "home.updatesDesc": "Stay informed with regular video updates about our work and impact.",
    "home.readyToMakeDifference": "Ready to Make a Difference?",
    "home.readyDesc":
      "Join thousands of donors who are already making an impact. Your ₹100 donation can help us continue our important work.",
    "home.joinNow": "Join Now",

    // Dashboard
    "dashboard.welcome": "Welcome back",
    "dashboard.thankYou": "Thank you for being part of our donation community.",
    "dashboard.totalDonations": "Total Donations",
    "dashboard.totalAmount": "Total Amount",
    "dashboard.joinMembership": "Join Membership / Donate ₹100",
    "dashboard.overview": "Overview",
    "dashboard.editProfile": "Edit Profile",
    "dashboard.donationHistory": "Donation History",
    "dashboard.monthlyReports": "Monthly Reports",
    "dashboard.profileInfo": "Profile Information",
    "dashboard.email": "Email",
    "dashboard.phone": "Phone",
    "dashboard.dateOfBirth": "Date of Birth",
    "dashboard.address": "Address",
    "dashboard.joinWhatsApp": "Join WhatsApp Community",
    "dashboard.whatsAppDesc": "Connect with other donors and get updates",
    "dashboard.scanQR": "Scan QR Code",
    "dashboard.orClickLink": "or click the link below",
    "dashboard.joinGroup": "Join Group",

    // Payment
    "payment.selectMethod": "Select Payment Method",
    "payment.razorpay": "Pay with Razorpay",
    "payment.upi": "Pay with UPI",
    "payment.processing": "Processing...",
    "payment.fixedAmount": "Fixed donation amount: ₹100",
    "payment.success": "Payment successful! Thank you for your contribution.",
    "payment.failed": "Payment verification failed",
    "payment.cancelled": "Payment cancelled",

    // Auth
    "auth.login": "Sign in to your account",
    "auth.register": "Create your account",
    "auth.fullName": "Full Name",
    "auth.emailAddress": "Email Address",
    "auth.phoneNumber": "Phone Number",
    "auth.password": "Password",
    "auth.confirmPassword": "Confirm Password",
    "auth.createAccount": "Create Account",
    "auth.signIn": "Sign in",
    "auth.alreadyHaveAccount": "Already have an account?",
    "auth.dontHaveAccount": "Don't have an account?",
    "auth.signUp": "Sign up",

    // Common
    "common.loading": "Loading...",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.update": "Update",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.close": "Close",
    "common.status": "Status",
    "common.date": "Date",
    "common.amount": "Amount",
  },
  hi: {
    // Header
    "header.home": "होम",
    "header.videos": "वीडियो",
    "header.dashboard": "डैशबोर्ड",
    "header.adminPanel": "एडमिन पैनल",
    "header.login": "लॉगिन",
    "header.register": "रजिस्टर",
    "header.logout": "लॉगआउट",

    // Home Page
    "home.title": "आज ही बदलाव लाएं",
    "home.subtitle":
      "दानदाताओं के हमारे समुदाय में शामिल हों और सकारात्मक बदलाव लाने में मदद करें। हर ₹100 का दान एक सार्थक प्रभाव डालता है।",
    "home.getStarted": "शुरू करें",
    "home.signIn": "साइन इन",
    "home.whyChoose": "हमारा प्लेटफॉर्म क्यों चुनें?",
    "home.whyChooseDesc":
      "हम दान देने और आपके प्रभाव को ट्रैक करने के लिए एक सुरक्षित, पारदर्शी और उपयोग में आसान प्लेटफॉर्म प्रदान करते हैं।",
    "home.fixedDonations": "निश्चित दान",
    "home.fixedDonationsDesc": "सरल ₹100 निश्चित दान राशि पारदर्शिता और निरंतरता सुनिश्चित करती है।",
    "home.community": "समुदाय",
    "home.communityDesc": "सकारात्मक प्रभाव डालने वाले समान विचारधारा वाले व्यक्तियों के समुदाय में शामिल हों।",
    "home.transparency": "पारदर्शिता",
    "home.transparencyDesc": "अपने दान को ट्रैक करें और समुदायिक प्रभाव की मासिक रिपोर्ट देखें।",
    "home.updates": "अपडेट",
    "home.updatesDesc": "हमारे काम और प्रभाव के बारे में नियमित वीडियो अपडेट के साथ सूचित रहें।",
    "home.readyToMakeDifference": "बदलाव लाने के लिए तैयार हैं?",
    "home.readyDesc":
      "हजारों दानदाताओं में शामिल हों जो पहले से ही प्रभाव डाल रहे हैं। आपका ₹100 का दान हमारे महत्वपूर्ण काम को जारी रखने में मदद कर सकता है।",
    "home.joinNow": "अभी शामिल हों",

    // Dashboard
    "dashboard.welcome": "वापसी पर स्वागत है",
    "dashboard.thankYou": "हमारे दान समुदाय का हिस्सा बनने के लिए धन्यवाद।",
    "dashboard.totalDonations": "कुल दान",
    "dashboard.totalAmount": "कुल राशि",
    "dashboard.joinMembership": "सदस्यता में शामिल हों / ₹100 दान करें",
    "dashboard.overview": "अवलोकन",
    "dashboard.editProfile": "प्रोफाइल संपादित करें",
    "dashboard.donationHistory": "दान इतिहास",
    "dashboard.monthlyReports": "मासिक रिपोर्ट",
    "dashboard.profileInfo": "प्रोफाइल जानकारी",
    "dashboard.email": "ईमेल",
    "dashboard.phone": "फोन",
    "dashboard.dateOfBirth": "जन्म तिथि",
    "dashboard.address": "पता",
    "dashboard.joinWhatsApp": "व्हाट्सऐप समुदाय में शामिल हों",
    "dashboard.whatsAppDesc": "अन्य दानदाताओं से जुड़ें और अपडेट प्राप्त करें",
    "dashboard.scanQR": "QR कोड स्कैन करें",
    "dashboard.orClickLink": "या नीचे दिए गए लिंक पर क्लिक करें",
    "dashboard.joinGroup": "ग्रुप में शामिल हों",

    // Payment
    "payment.selectMethod": "भुगतान विधि चुनें",
    "payment.razorpay": "Razorpay से भुगतान करें",
    "payment.upi": "UPI से भुगतान करें",
    "payment.processing": "प्रसंस्करण...",
    "payment.fixedAmount": "निश्चित दान राशि: ₹100",
    "payment.success": "भुगतान सफल! आपके योगदान के लिए धन्यवाद।",
    "payment.failed": "भुगतान सत्यापन असफल",
    "payment.cancelled": "भुगतान रद्द",

    // Auth
    "auth.login": "अपने खाते में साइन इन करें",
    "auth.register": "अपना खाता बनाएं",
    "auth.fullName": "पूरा नाम",
    "auth.emailAddress": "ईमेल पता",
    "auth.phoneNumber": "फोन नंबर",
    "auth.password": "पासवर्ड",
    "auth.confirmPassword": "पासवर्ड की पुष्टि करें",
    "auth.createAccount": "खाता बनाएं",
    "auth.signIn": "साइन इन",
    "auth.alreadyHaveAccount": "पहले से खाता है?",
    "auth.dontHaveAccount": "खाता नहीं है?",
    "auth.signUp": "साइन अप",

    // Common
    "common.loading": "लोड हो रहा है...",
    "common.save": "सेव करें",
    "common.cancel": "रद्द करें",
    "common.update": "अपडेट करें",
    "common.delete": "हटाएं",
    "common.edit": "संपादित करें",
    "common.close": "बंद करें",
    "common.status": "स्थिति",
    "common.date": "तारीख",
    "common.amount": "राशि",
  },
  te: {
    // Header
    "header.home": "హోమ్",
    "header.videos": "వీడియోలు",
    "header.dashboard": "డాష్‌బోర్డ్",
    "header.adminPanel": "అడ్మిన్ ప్యానెల్",
    "header.login": "లాగిన్",
    "header.register": "రిజిస్టర్",
    "header.logout": "లాగ్అవుట్",

    // Home Page
    "home.title": "ఈరోజే మార్పు తీసుకురండి",
    "home.subtitle":
      "దాతల మా కమ్యూనిటీలో చేరండి మరియు సానుకూల మార్పును తీసుకురావడంలో సహాయపడండి। ప్రతి ₹100 విరాళం అర్థవంతమైన ప్రభావాన్ని చూపుతుంది.",
    "home.getStarted": "ప్రారంభించండి",
    "home.signIn": "సైన్ ఇన్",
    "home.whyChoose": "మా ప్లాట్‌ఫారమ్‌ను ఎందుకు ఎంచుకోవాలి?",
    "home.whyChooseDesc":
      "మేము విరాళాలు ఇవ్వడానికి మరియు మీ ప్రభావాన్ని ట్రాక్ చేయడానికి సురక్షితమైన, పారదర్శకమైన మరియు ఉపయోగించడానికి సులభమైన ప్లాట్‌ఫారమ్‌ను అందిస్తాము.",
    "home.fixedDonations": "స్థిర విరాళాలు",
    "home.fixedDonationsDesc": "సాధారణ ₹100 స్థిర విరాళ మొత్తం పారదర్శకత మరియు స్థిరత్వాన్ని నిర్ధారిస్తుంది.",
    "home.community": "కమ్యూనిటీ",
    "home.communityDesc": "సానుకూల ప్రభావాన్ని చూపే సమాన ఆలోచనలు కలిగిన వ్యక్తుల కమ్యూనిటీలో చేరండి.",
    "home.transparency": "పారదర్శకత",
    "home.transparencyDesc": "మీ విరాళాలను ట్రాక్ చేయండి మరియు కమ్యూనిటీ ప్రభావం యొక్క నెలవారీ నివేదికలను చూడండి.",
    "home.updates": "అప్‌డేట్‌లు",
    "home.updatesDesc": "మా పని మరియు ప్రభావం గురించి క్రమం వారీ వీడియో అప్‌డేట్‌లతో సమాచారంతో ఉండండి.",
    "home.readyToMakeDifference": "మార్పు తీసుకురావడానికి సిద్ధంగా ఉన్నారా?",
    "home.readyDesc": "ఇప్పటికే ప్రభావం చూపుతున్న వేలాది మంది దాతలతో చేరండి। మీ ₹100 విరాళం మా ముఖ్యమైన పనిని కొనసాగించడంలో సహాయపడుతుంది.",
    "home.joinNow": "ఇప్పుడే చేరండి",

    // Dashboard
    "dashboard.welcome": "తిరిగి స్వాగతం",
    "dashboard.thankYou": "మా విరాళ కమ్యూనిటీలో భాగం కావడానికి ధన్యవాదాలు.",
    "dashboard.totalDonations": "మొత్తం విరాళాలు",
    "dashboard.totalAmount": "మొత్తం మొత్తం",
    "dashboard.joinMembership": "సభ్యత్వంలో చేరండి / ₹100 విరాళం ఇవ్వండి",
    "dashboard.overview": "అవలోకనం",
    "dashboard.editProfile": "ప్రొఫైల్ సవరించండి",
    "dashboard.donationHistory": "విరాళ చరిత్ర",
    "dashboard.monthlyReports": "నెలవారీ నివేదికలు",
    "dashboard.profileInfo": "ప్రొఫైల్ సమాచారం",
    "dashboard.email": "ఇమెయిల్",
    "dashboard.phone": "ఫోన్",
    "dashboard.dateOfBirth": "పుట్టిన తేదీ",
    "dashboard.address": "చిరునామా",
    "dashboard.joinWhatsApp": "వాట్సాప్ కమ్యూనిటీలో చేరండి",
    "dashboard.whatsAppDesc": "ఇతర దాతలతో కనెక్ట్ అవ్వండి మరియు అప్‌డేట్‌లను పొందండి",
    "dashboard.scanQR": "QR కోడ్ స్కాన్ చేయండి",
    "dashboard.orClickLink": "లేదా క్రింది లింక్‌పై క్లిక్ చేయండి",
    "dashboard.joinGroup": "గ్రూప్‌లో చేరండి",

    // Payment
    "payment.selectMethod": "చెల్లింపు పద్ధతిని ఎంచుకోండి",
    "payment.razorpay": "Razorpay తో చెల్లించండి",
    "payment.upi": "UPI తో చెల్లించండి",
    "payment.processing": "ప్రాసెసింగ్...",
    "payment.fixedAmount": "స్థిర విరాళ మొత్తం: ₹100",
    "payment.success": "చెల్లింపు విజయవంతం! మీ సహకారానికి ధన్యవాదాలు.",
    "payment.failed": "చెల్లింపు ధృవీకరణ విఫలమైంది",
    "payment.cancelled": "చెల్లింపు రద్దు చేయబడింది",

    // Auth
    "auth.login": "మీ ఖాతాలోకి సైన్ ఇన్ చేయండి",
    "auth.register": "మీ ఖాతాను సృష్టించండి",
    "auth.fullName": "పూర్తి పేరు",
    "auth.emailAddress": "ఇమెయిల్ చిరునామా",
    "auth.phoneNumber": "ఫోన్ నంబర్",
    "auth.password": "పాస్‌వర్డ్",
    "auth.confirmPassword": "పాస్‌వర్డ్ నిర్ధారించండి",
    "auth.createAccount": "ఖాతా సృష్టించండి",
    "auth.signIn": "సైన్ ఇన్",
    "auth.alreadyHaveAccount": "ఇప్పటికే ఖాతా ఉందా?",
    "auth.dontHaveAccount": "ఖాతా లేదా?",
    "auth.signUp": "సైన్ అప్",

    // Common
    "common.loading": "లోడ్ అవుతోంది...",
    "common.save": "సేవ్ చేయండి",
    "common.cancel": "రద్దు చేయండి",
    "common.update": "అప్‌డేట్ చేయండి",
    "common.delete": "తొలగించండి",
    "common.edit": "సవరించండి",
    "common.close": "మూసివేయండి",
    "common.status": "స్థితి",
    "common.date": "తేదీ",
    "common.amount": "మొత్తం",
  },
  ta: {
    // Header
    "header.home": "முகப்பு",
    "header.videos": "வீடியோக்கள்",
    "header.dashboard": "டாஷ்போர்டு",
    "header.adminPanel": "நிர்வாக பேனல்",
    "header.login": "உள்நுழைவு",
    "header.register": "பதிவு",
    "header.logout": "வெளியேறு",

    // Home Page
    "home.title": "இன்றே மாற்றத்தை கொண்டு வாருங்கள்",
    "home.subtitle":
      "நன்கொடையாளர்களின் எங்கள் சமூகத்தில் சேர்ந்து நேர்மறையான மாற்றத்தை கொண்டு வர உதவுங்கள். ஒவ்வொரு ₹100 நன்கொடையும் அர்த்தமுள்ள தாக்கத்தை ஏற்படுத்துகிறது.",
    "home.getStarted": "தொடங்குங்கள்",
    "home.signIn": "உள்நுழைக",
    "home.whyChoose": "எங்கள் தளத்தை ஏன் தேர்வு செய்ய வேண்டும்?",
    "home.whyChooseDesc":
      "நன்கொடை வழங்குவதற்கும் உங்கள் தாக்கத்தை கண்காணிப்பதற்கும் பாதுகாப்பான, வெளிப்படையான மற்றும் பயன்படுத்த எளிதான தளத்தை நாங்கள் வழங்குகிறோம்.",
    "home.fixedDonations": "நிலையான நன்கொடைகள்",
    "home.fixedDonationsDesc": "எளிய ₹100 நிலையான நன்கொடை தொகை வெளிப்படைத்தன்மை மற்றும் நிலைத்தன்மையை உறுதி செய்கிறது.",
    "home.community": "சமூகம்",
    "home.communityDesc": "நேர்மறையான தாக்கத்தை ஏற்படுத்தும் ஒத்த எண்ணம் கொண்ட நபர்களின் சமூகத்தில் சேருங்கள்.",
    "home.transparency": "வெளிப்படைத்தன்மை",
    "home.transparencyDesc": "உங்கள் நன்கொடைகளை கண்காணித்து சமூக தாக்கத்தின் மாதாந்திர அறிக்கைகளைப் பார்க்கவும்.",
    "home.updates": "புதுப்பிப்புகள்",
    "home.updatesDesc": "எங்கள் பணி மற்றும் தாக்கம் பற்றிய வழக்கமான வீடியோ புதுப்பிப்புகளுடன் தகவலறிந்து இருங்கள்.",
    "home.readyToMakeDifference": "மாற்றத்தை ஏற்படுத்த தயாரா?",
    "home.readyDesc":
      "ஏற்கனவே தாக்கத்தை ஏற்படுத்தும் ஆயிரக்கணக்கான நன்கொடையாளர்களுடன் சேருங்கள். உங்கள் ₹100 நன்கொடை எங்கள் முக்கியமான பணியைத் தொடர உதவும்.",
    "home.joinNow": "இப்போதே சேருங்கள்",

    // Dashboard
    "dashboard.welcome": "மீண்டும் வரவேற்கிறோம்",
    "dashboard.thankYou": "எங்கள் நன்கொடை சமூகத்தின் ஒரு பகுதியாக இருப்பதற்கு நன்றி.",
    "dashboard.totalDonations": "மொத்த நன்கொடைகள்",
    "dashboard.totalAmount": "மொத்த தொகை",
    "dashboard.joinMembership": "உறுப்பினர் சேர / ₹100 நன்கொடை",
    "dashboard.overview": "கண்ணோட்டம்",
    "dashboard.editProfile": "சுயவிவரத்தை திருத்து",
    "dashboard.donationHistory": "நன்கொடை வரலாறு",
    "dashboard.monthlyReports": "மாதாந்திர அறிக்கைகள்",
    "dashboard.profileInfo": "சுயவிவர தகவல்",
    "dashboard.email": "மின்னஞ்சல்",
    "dashboard.phone": "தொலைபேசி",
    "dashboard.dateOfBirth": "பிறந்த தேதி",
    "dashboard.address": "முகவரி",
    "dashboard.joinWhatsApp": "WhatsApp சமூகத்தில் சேருங்கள்",
    "dashboard.whatsAppDesc": "மற்ற நன்கொடையாளர்களுடன் இணைந்து புதுப்பிப்புகளைப் பெறுங்கள்",
    "dashboard.scanQR": "QR குறியீட்டை ஸ்கேன் செய்யுங்கள்",
    "dashboard.orClickLink": "அல்லது கீழே உள்ள இணைப்பைக் கிளிக் செய்யவும்",
    "dashboard.joinGroup": "குழுவில் சேருங்கள்",

    // Payment
    "payment.selectMethod": "கட்டண முறையைத் தேர்ந்தெடுக்கவும்",
    "payment.razorpay": "Razorpay மூலம் பணம் செலுத்துங்கள்",
    "payment.upi": "UPI மூலம் பணம் செலுத்துங்கள்",
    "payment.processing": "செயலாக்கம்...",
    "payment.fixedAmount": "நிலையான நன்கொடை தொகை: ₹100",
    "payment.success": "கட்டணம் வெற்றிகரமாக! உங்கள் பங்களிப்புக்கு நன்றி.",
    "payment.failed": "கட்டண சரிபார்ப்பு தோல்வியடைந்தது",
    "payment.cancelled": "கட்டணம் ரத்து செய்யப்பட்டது",

    // Auth
    "auth.login": "உங்கள் கணக்கில் உள்நுழையவும்",
    "auth.register": "உங்கள் கணக்கை உருவாக்கவும்",
    "auth.fullName": "முழு பெயர்",
    "auth.emailAddress": "மின்னஞ்சல் முகவரி",
    "auth.phoneNumber": "தொலைபேசி எண்",
    "auth.password": "கடவுச்சொல்",
    "auth.confirmPassword": "கடவுச்சொல்லை உறுதிப்படுத்தவும்",
    "auth.createAccount": "கணக்கை உருவாக்கவும்",
    "auth.signIn": "உள்நுழைக",
    "auth.alreadyHaveAccount": "ஏற்கனவே கணக்கு உள்ளதா?",
    "auth.dontHaveAccount": "கணக்கு இல்லையா?",
    "auth.signUp": "பதிவு செய்யவும்",

    // Common
    "common.loading": "ஏற்றுகிறது...",
    "common.save": "சேமிக்கவும்",
    "common.cancel": "ரத்து செய்யவும்",
    "common.update": "புதுப்பிக்கவும்",
    "common.delete": "நீக்கவும்",
    "common.edit": "திருத்தவும்",
    "common.close": "மூடவும்",
    "common.status": "நிலை",
    "common.date": "தேதி",
    "common.amount": "தொகை",
  },
}

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get saved language from localStorage or default to 'en'
    return localStorage.getItem("selectedLanguage") || "en"
  })

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("selectedLanguage", langCode)
  }

  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations.en[key] || key
  }

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    availableLanguages: [
      { code: "en", name: "English", flag: "🇺🇸" },
      { code: "hi", name: "हिंदी", flag: "🇮🇳" },
      { code: "te", name: "తెలుగు", flag: "🇮🇳" },
      { code: "ta", name: "தமிழ்", flag: "🇮🇳" },
    ],
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
